import React, { useEffect, useState } from "react";
import CustomSnackbar from "../../Feedback/CustomSnackbar";
import CustomSplitColumnCard from "../../misc/CustomSplitColumnCard";
import HttpFetchStatus from "../../../enums/api/http-fetch-status";
import getSaveErrorMessages from "../../../functions/general/get-save-error-messages";
import { ButtonsIcon, SaveIcon } from "../../../utils/icons/icons";
import Divider from "@mui/material/Divider";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { useSharedActions, useSharedDispatch, useSharedSelector } from "../../../store/SharedProvider";
import {
  selectGlobalMissions,
  selectMissionsRestSet,
  selectRoomButtonConfiguration,
  selectRoomsRestSet,
  selectSelectedRoomId,
  selectSelectedRoomMissions,
} from "../../../store/room/room-selectors";
import { selectRobotCleaningCommandButtons } from "../../../store/robot-configurations/robot-configurations-selectors";
import ButtonRow from "../../Configuration/ButtonConfiguration/ButtonRow";
import DialogWrapperWithClose from "../../../components/General/DialogWrapperWithClose";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import ReadOnlyProtectedButton from "../../../components/Inputs/ReadOnlyProtectedButton";
import CleaningCommandButton, { mapButtonIdToName } from "../../../interfaces/robot/buttons/command-buttons";
import ScheduleEntryMission from "../Scheduler/SchedulerEntry/ScheduleEntryMission";
import ToggleButton from "@mui/material/ToggleButton";
import { getNavigatorDescription } from "../../../interfaces/robot/navigator";

const RoomButtonConfiguration: React.FC = () => {
  const { t } = useTranslation();

  const dispatch = useSharedDispatch();

  const { patchRoomsRestSet, getButtonConfiguration, getGlobalMissions, patchRoomById } = useSharedActions();

  const roomButtonConfiguration = useSharedSelector((state) => selectRoomButtonConfiguration(state));
  const roomRestSet = useSharedSelector((state) => selectRoomsRestSet(state));
  const roomMissions = useSharedSelector((state) => selectSelectedRoomMissions(state));
  const globalMissions = useSharedSelector((state) => selectGlobalMissions(state));
  const missionsRestSet = useSharedSelector((state) => selectMissionsRestSet(state));
  const globalCommandButtons = useSharedSelector((state) => selectRobotCleaningCommandButtons(state));
  const roomId = useSharedSelector((state) => selectSelectedRoomId(state));

  const [saveLock, setSaveLock] = useState(false);
  const [editedButton, setEditedButton] = useState<CleaningCommandButton | null>(null);

  useEffect(() => {
    if (globalCommandButtons.rest.getStatus === HttpFetchStatus.Idle) {
      dispatch(getButtonConfiguration());
    }
  }, [dispatch, getButtonConfiguration, globalCommandButtons.rest]);

  useEffect(() => {
    if (missionsRestSet.getStatus === HttpFetchStatus.Idle) {
      dispatch(getGlobalMissions());
    }
  }, [dispatch, getGlobalMissions, missionsRestSet.getStatus]);

  useEffect(() => {
    if (roomRestSet.patchStatus === HttpFetchStatus.Success) {
      setEditedButton(null);
    }
  }, [roomRestSet]);

  const snackbarCloseHandler = () => {
    setSaveLock(false);
    patchRoomsRestSet({ patchStatus: HttpFetchStatus.Idle, patchError: null });
  };

  const configButtonClickHandler = (button: CleaningCommandButton) => {
    setEditedButton(button);
  };

  const closeEditButtonHandler = () => {
    setEditedButton(null);
  };

  const saveClickHandler = () => {
    if (roomId != null && editedButton != null) {
      setSaveLock(true);
      dispatch(patchRoomById({ id: roomId, data: { buttonConfiguration: [editedButton] } }));
    }
  };

  const missionIdChangeHandler = (newId: string) => {
    const isGlobalMission = globalMissions.some((m) => m.id === newId);
    setEditedButton((prevState) =>
      prevState != null ? { ...prevState, missionId: isGlobalMission ? null : newId } : null
    );
  };

  const globalMissionIdForSelectedButton =
    globalCommandButtons.data?.find((b) => b.id === editedButton?.id)?.missionId ?? "";

  return (
    <React.Fragment>
      <CustomSplitColumnCard
        title={t("configuration-buttons")}
        content={[
          {
            left: (
              <Stack spacing={1} pr={2}>
                <Stack direction="row" alignItems="center" spacing={1} py={1}>
                  <ButtonsIcon />
                  <Typography>{t("robot-command-buttons")}</Typography>
                </Stack>
              </Stack>
            ),
            right: (
              <Stack spacing={2} pb={1} divider={<Divider flexItem />}>
                {roomButtonConfiguration == null ||
                globalCommandButtons.data === null ||
                globalMissions.length === 0 ? (
                  <React.Fragment>
                    {[1, 2, 3, 4, 5, 6].map((index) => (
                      <Stack direction="row" key={index}>
                        <Stack flexGrow={1}>
                          <Skeleton variant="text" sx={(theme) => ({ height: theme.spacing(3), width: "20%" })} />
                          <Skeleton variant="text" sx={(theme) => ({ height: theme.spacing(4), width: "50%" })} />
                        </Stack>
                        <Stack alignItems="center" spacing={1} direction="row">
                          <Skeleton
                            variant="circular"
                            sx={(theme) => ({ height: theme.spacing(5), width: theme.spacing(5) })}
                          />
                        </Stack>
                      </Stack>
                    ))}
                  </React.Fragment>
                ) : (
                  roomButtonConfiguration.map((button) => {
                    let mission = roomMissions.find((m) => m.id === button.missionId) ?? null;
                    if (mission == null) {
                      const globalMissionId =
                        globalCommandButtons.data?.find((b) => b.id === button.id)?.missionId ?? null;
                      if (globalMissionId !== null) {
                        mission = globalMissions.find((m) => m.id === globalMissionId) ?? null;
                      }
                    }
                    if (mission == null) return null;
                    return (
                      <ButtonRow
                        key={button.id}
                        cleaningButton={button}
                        onClick={() => configButtonClickHandler(button)}
                        mission={mission}
                      />
                    );
                  })
                )}
              </Stack>
            ),
          },
        ]}
      />
      <DialogWrapperWithClose
        onClose={closeEditButtonHandler}
        open={editedButton != null}
        title={`${t("configure-button")} - ${t(mapButtonIdToName(editedButton?.id ?? ""))}`}
        fullWidth
        maxWidth="sm"
      >
        <DialogContent>
          {editedButton != null && roomId != null && (
            <ScheduleEntryMission
              roomId={roomId}
              CustomFixedButton={
                <ToggleButton
                  value={globalMissionIdForSelectedButton}
                  sx={(theme) => ({
                    fontSize: "0.87rem",
                    fontWeight: "bold",
                    color:
                      editedButton.missionId === globalMissionIdForSelectedButton
                        ? `${theme.palette.primary.main}CC !important`
                        : "GrayText",
                    textTransform: "none",
                    textAlign: "left",
                  })}
                >
                  {getNavigatorDescription(
                    globalMissions.find((m) => m.id === globalMissionIdForSelectedButton)?.mode ?? ""
                  )}
                </ToggleButton>
              }
              missionId={editedButton?.missionId ?? globalMissionIdForSelectedButton}
              onMissionIdChange={missionIdChangeHandler}
              helpText={t("form-help-text-button-mission-selection")}
            />
          )}
        </DialogContent>
        <DialogActions>
          <ReadOnlyProtectedButton
            variant="contained"
            color="primary"
            size="small"
            startIcon={<SaveIcon />}
            onClick={saveClickHandler}
          >
            {t("button-save")}
          </ReadOnlyProtectedButton>
        </DialogActions>
      </DialogWrapperWithClose>
      <CustomSnackbar
        title={t("error-saving-buttonconfiguration")}
        open={saveLock && roomRestSet.patchStatus === HttpFetchStatus.Failed}
        onClose={snackbarCloseHandler}
        textMessages={getSaveErrorMessages(roomRestSet.patchError)}
        severity="error"
      />
      <CustomSnackbar
        title={t("success-saving-buttonconfiguration")}
        severity="success"
        open={saveLock && roomRestSet.patchStatus === HttpFetchStatus.Success}
        onClose={snackbarCloseHandler}
      />
    </React.Fragment>
  );
};

export default RoomButtonConfiguration;
